import React from "react";
import { appWithTranslation } from "next-i18next";
import NextI18NextConfig from "../next-i18next.config";
import App from "next/app";
import { Provider } from "react-redux";
import { wrapper } from "@lib/store";
import Bugsnag from "@lib/bugsnag";
import "@algolia/autocomplete-theme-classic";
import customInitStoryblok from "@lib/storyblok";
import { AxiosInterceptor } from "@lib/axios";
import AppInit from "@lib/AppInit";
import ToastConfig from "@utils/toasts/ToastConfig";
import UserListManager from "@components/views/UserListManager";
import Layout from "@components/layout/Layout";
import Error from "./_error";
import { getFeatures } from "@actions/features-actions";
import { getAdvertising } from "@actions/advertising-actions";
import "swiper/css";
import "swiper/css/pagination";
import "react-dates/lib/css/_datepicker.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";

// eslint-disable-next-line import/no-dynamic-require
require(`../styles/index-${process.env.NEXT_PUBLIC_CUSTOMER}.scss`);

customInitStoryblok();

function MyApp({ Component, ...rest }) {
  // See here for the message : Bugsnag.start() was called more than once.
  // https://github.com/bugsnag/bugsnag-js/issues/811
  const ErrorBoundary = Bugsnag.getPlugin("react").createErrorBoundary(React);
  const { store, props } = wrapper.useWrappedStore(rest);
  const headerBehaviors = props.pageProps.headerBehaviors || {};

  const unauthRequired = !!Component.requireNoAuth;
  const authRequired =
    Component.requireAuth || (!Component.requireNoAuth && Component.requireAuth !== false);

  return (
    <ErrorBoundary FallbackComponent={Error}>
      <Provider store={store}>
        <AxiosInterceptor>
          <Layout headerBehaviors={headerBehaviors}>
            <AppInit
              unauthRequired={unauthRequired}
              authRequired={authRequired}
            >
              <Component {...props.pageProps} />
              <ToastConfig />
              <UserListManager />
            </AppInit>
          </Layout>
        </AxiosInterceptor>
      </Provider>
    </ErrorBoundary>
  );
}

MyApp.getInitialProps = wrapper.getInitialAppProps((store) => async ({ Component, ctx }) => {
  const areLogsEnabled = ctx?.router?.query?.debug || "";
  global.areLogsEnabled = areLogsEnabled === "true";
  const { host, xproxiedfromwww } = ctx.req?.headers || {};
  if (
    (host === "app.lecloset.fr" || host === "app.legranddressing.com") &&
    xproxiedfromwww === undefined
  ) {
    ctx.res.writeHead(301, {
      Location: `https://${host.replace("app", "www")}${ctx.req.url}`,
    });
    ctx.res.end();
    return null;
  }

  await Promise.all([store.dispatch(getFeatures()), store.dispatch(getAdvertising())]);

  const appProps = await App.getInitialProps({ Component, ctx });
  return {
    ...appProps,
  };
});

export default appWithTranslation(MyApp, NextI18NextConfig);
